/**
 * PivotTablePanel.tsx
 *
 * Renders a `pivot_table` panel per brand guide §27 and panel-library-design §4.4.
 *
 * Layout rules:
 *   - ≤ 4 columns → col-span-2, no scroll
 *   - 5–8 columns → col-span-2 with horizontal scroll inside table
 *   - ≥ 9 columns → col-span-3 (caller controls via `colSpanClass` prop)
 *   - ≥ 12 columns OR > 20 rows → caller must render FallbackPanelType instead
 *
 * Cell alignment:
 *   - Row dimension labels → text-left font-semibold text-primary
 *   - Numeric cells → text-right tabular-nums
 *   - Zero values → muted em-dash ("—"), not "0"
 *
 * Totals:
 *   - Row totals in rightmost column (font-semibold)
 *   - Col totals in bottom row (font-semibold)
 *   - Grand-total cell (bg-muted text-primary font-bold)
 *
 * A11y (brand guide §16 req 2):
 *   - sr-only companion flat table with one row per non-zero cell
 */

import React from 'react'
import { Info } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { PivotTableDisplayData } from '@/types/DashboardV1.types'

interface PivotTablePanelProps {
  data: PivotTableDisplayData
  panelTitle: string
  /**
   * Owning `panelData.panelId` — required to derive a per-panel-unique tooltip
   * content id so two pivots referencing the same `tcs.*` field on one
   * dashboard emit distinct DOM ids and `aria-describedby` resolves
   * deterministically per WAI-ARIA. Making this required (not optional) ensures
   * a future caller cannot forget it and inadvertently produce duplicate ids.
   *
   * EPIC-131 / Slice 1.2.
   */
  panelId: string
  /**
   * TCS-derived field definitions from the panel's wire data.
   * When present and any key corresponds to the pivot column axis,
   * an info-affordance icon is rendered in the column-group header row.
   *
   * EPIC-131 / Slice 1.2.
   */
  fieldDefinitions?: Record<string, { definition: string; displayName: string }>
}

/** Format a numeric cell value — zero becomes an em-dash. */
function formatCell(value: number): string {
  if (value === 0) return '—'
  return value.toLocaleString('en-US')
}

export const PivotTablePanel: React.FC<PivotTablePanelProps> = ({
  data,
  panelTitle,
  panelId,
  fieldDefinitions,
}) => {
  const { columnLabels, rowDimLabel, rows, rowTotals, colTotals, grandTotal } = data

  const isEmpty = rows.length === 0
  const needsScroll = columnLabels.length >= 5

  const thClass = 'text-[10.5px] font-semibold text-secondary uppercase tracking-wide bg-muted border-b border-border px-2 py-1.5 text-right'
  const thFirstClass = 'text-[10.5px] font-semibold text-secondary uppercase tracking-wide bg-muted border-b border-border px-2 py-1.5 text-left sticky left-0 bg-card border-r border-border'
  const tdFirstClass = 'text-xs font-semibold text-primary px-2 py-1.5 sticky left-0 bg-card border-r border-border'
  const tdNumClass = 'text-xs text-right tabular-nums px-2 py-1.5 text-secondary'
  const tdTotalClass = 'text-xs font-semibold text-right tabular-nums px-2 py-1.5 text-primary'
  const tdGrandTotalClass = 'text-xs font-bold text-right tabular-nums px-2 py-1.5 bg-muted text-primary'

  // Determine if any tcs.* field definition maps to the column axis.
  // In v1 there is at most one tcs.* field per pivot (the colField).
  // The FE reads the first key from fieldDefinitions that starts with 'tcs.'.
  // Empty-definition entries are skipped — a defective BE payload gets no icon
  // rather than an icon with a blank tooltip body.
  const tcsColEntry = Object.entries(fieldDefinitions ?? {}).find(
    ([key, value]) => key.startsWith('tcs.') && value.definition
  )

  // Per-panel-unique tooltip id — see PanelRenderer.tsx TcsDefinitionTooltip
  // doc for the WAI-ARIA rationale (multiple panels referencing the same
  // tcs.* field MUST emit distinct DOM ids).
  const pivotTooltipId = tcsColEntry
    ? `tcs-def-pivot-${panelId}-${tcsColEntry[0].replace(/\./g, '-')}`
    : ''

  return (
    <>
      {/* Main visual pivot table */}
      <div className={needsScroll ? 'overflow-x-auto' : undefined}>
        <Table aria-label={`Pivot table: ${panelTitle}`}>
          <TableHeader>
            {/* Column-group header row — only rendered when a tcs.* col field is present.
                Mockup §3.6.C: info icon inside the <th> after the axis label text. */}
            {tcsColEntry && (
              <TableRow>
                {/* Empty corner cell */}
                <TableHead className={thFirstClass} />
                {/* Column group header: spans all data columns, with info icon */}
                <TableHead
                  colSpan={columnLabels.length}
                  className="text-[10.5px] font-semibold text-secondary uppercase tracking-wide bg-muted border-b border-border px-2 py-1.5 text-center"
                >
                  <div className="inline-flex items-center gap-1 justify-center">
                    <span>{tcsColEntry[1].displayName}</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center w-4 h-4 rounded text-muted-foreground hover:text-accent focus-visible:text-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors bg-transparent border-0 cursor-pointer p-0"
                            aria-label={`About ${tcsColEntry[1].displayName} field`}
                            aria-describedby={pivotTooltipId}
                          >
                            <Info size={14} aria-hidden="true" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          id={pivotTooltipId}
                          side="top"
                          className="max-w-[280px] p-4"
                        >
                          {/* Brand-guide §35: text-blue-300 is the permitted exception
                              to the no-raw-Tailwind-color rule on the dark popover. */}
                          <p className="text-[10px] font-semibold tracking-wider mb-2 uppercase text-blue-300">
                            TCS-derived field
                          </p>
                          {/* Divider — brand-guide §35 specifies rgba(248, 250, 252, 0.12).
                              No Tailwind utility maps to this exact alpha-on-slate value. */}
                          <div
                            className="h-px mb-2.5"
                            style={{ background: 'rgba(248, 250, 252, 0.12)' }}
                          />
                          <p className="text-sm leading-relaxed text-popover-foreground font-normal normal-case">
                            {tcsColEntry[1].definition}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </TableHead>
                {/* Row totals header placeholder */}
                <TableHead className={thClass}>Total</TableHead>
              </TableRow>
            )}
            <TableRow>
              {/* Row dimension header (sticky left) */}
              <TableHead className={thFirstClass}>{rowDimLabel}</TableHead>
              {/* Column labels */}
              {columnLabels.map(col => (
                <TableHead key={col} className={thClass}>{col}</TableHead>
              ))}
              {/* Row totals header */}
              <TableHead className={thClass}>Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isEmpty ? (
              <TableRow>
                <TableCell
                  colSpan={columnLabels.length + 2}
                  className="text-center text-xs text-secondary py-4"
                >
                  No data available for current filters
                </TableCell>
              </TableRow>
            ) : (
              <>
                {rows.map((row, rowIdx) => (
                  <TableRow key={rowIdx} className="hover:bg-muted/40">
                    {/* Row dimension label */}
                    <TableCell className={tdFirstClass}>{row.rowLabel}</TableCell>
                    {/* Cell values */}
                    {row.values.map((val, colIdx) => (
                      <TableCell key={colIdx} className={tdNumClass}>
                        {formatCell(val)}
                      </TableCell>
                    ))}
                    {/* Row total */}
                    <TableCell className={tdTotalClass}>
                      {rowTotals[rowIdx] !== undefined
                        ? formatCell(rowTotals[rowIdx])
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Column totals row */}
                <TableRow>
                  <TableCell className={`${tdFirstClass} font-bold`}>Total</TableCell>
                  {colTotals.map((total, colIdx) => (
                    <TableCell key={colIdx} className={tdTotalClass}>
                      {formatCell(total)}
                    </TableCell>
                  ))}
                  {/* Grand total */}
                  <TableCell className={tdGrandTotalClass}>
                    {formatCell(grandTotal)}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </div>

      {/* sr-only companion flat table — brand guide §16 req 2.
          3-column schema: Row / Column / Value.
          One row per non-zero data cell, followed by row totals, col totals, grand total. */}
      <table className="sr-only">
        <caption>{panelTitle} — pivot data table</caption>
        <thead>
          <tr>
            <th scope="col">Row</th>
            <th scope="col">Column</th>
            <th scope="col">Value</th>
          </tr>
        </thead>
        <tbody>
          {/* Data cells (non-zero only) */}
          {rows.map((row, rowIdx) =>
            row.values.map((val, colIdx) =>
              val !== 0 ? (
                <tr key={`cell-${rowIdx}-${colIdx}`}>
                  <td>{row.rowLabel}</td>
                  <td>{columnLabels[colIdx]}</td>
                  <td>{val.toLocaleString('en-US')}</td>
                </tr>
              ) : null
            )
          )}
          {/* Row totals */}
          {rows.map((row, rowIdx) =>
            rowTotals[rowIdx] !== undefined && rowTotals[rowIdx] !== 0 ? (
              <tr key={`row-total-${rowIdx}`}>
                <td>{row.rowLabel}</td>
                <td>Total</td>
                <td>{rowTotals[rowIdx].toLocaleString('en-US')}</td>
              </tr>
            ) : null
          )}
          {/* Column totals */}
          {columnLabels.map((col, colIdx) =>
            colTotals[colIdx] !== undefined && colTotals[colIdx] !== 0 ? (
              <tr key={`col-total-${colIdx}`}>
                <td>Total</td>
                <td>{col}</td>
                <td>{colTotals[colIdx].toLocaleString('en-US')}</td>
              </tr>
            ) : null
          )}
          {/* Grand total */}
          {grandTotal !== 0 && (
            <tr key="grand-total">
              <td>Total</td>
              <td>Total</td>
              <td>{grandTotal.toLocaleString('en-US')}</td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  )
}
